// Pure helpers for the share landing (`/c/<token>` — Brief 3b NEEDS-5).
//
// The backend mints share links as `<app_link_base>/c/<token>`. Native apps
// route them via App Clip / App Links; THIS page is the web lane — the
// no-install path for a laptop or an app-less Android phone. The exchange is
// the existing `POST /auth/share` (a thread-scoped JWT for ONE thread:
// messages + the live stream, nothing else), so everything here is plumbing
// around seams that already exist.
//
// Two deliberate choices, both load-bearing:
//   - the guest's scoped session lives in an IN-MEMORY TokenStore, never
//     localStorage — opening a share link must not clobber an orchestrator's
//     own session in the same browser, and a thread-scoped credential has no
//     business outliving the tab;
//   - the guest's "you" lane comes from the scoped JWT's own email claim
//     (decoded locally, display-only — authorization stays server-side).

import type { AuthResponse, SessionUser, TokenStore } from "@waytide/core";

/** An in-memory `TokenStore`: the share guest's scoped session lives and dies
 * with the page. Same seam the web/RN stores fill — the shared client treats
 * it identically. */
export function memoryTokenStore(): TokenStore {
  let token: string | null = null;
  let identity: string | null = null;
  let user: SessionUser | null = null;
  return {
    getToken: () => token,
    getIdentity: () => identity,
    getUser: () => user,
    setSession: (resp: AuthResponse) => {
      token = resp.token;
      identity = resp.identity;
      user = resp.user;
    },
    clear: () => {
      token = null;
      identity = null;
      user = null;
    },
  };
}

/** The `email` claim from a JWT payload, or "" when absent/undecodable.
 * DISPLAY-ONLY (drives the chat's "you" lane) — authorization is the
 * server's; nothing here trusts the decode. Tolerates base64url and missing
 * padding; never throws. */
export function emailFromJwt(jwt: string): string {
  const parts = jwt.split(".");
  if (parts.length < 2) return "";
  const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
  const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
  try {
    const payload = JSON.parse(atob(padded)) as Record<string, unknown>;
    return typeof payload.email === "string" ? payload.email : "";
  } catch {
    return "";
  }
}
